// Browser polyfills
import '@babel/polyfill';
import 'whatwg-fetch';

// Libs
import React, { useState } from 'react';
import { createRoot } from 'react-dom/client';
import { Layout, Result } from '@nackle/paper';
import NackleMenu from '@nackle/menu';
const { Content } = Layout;

// Routing
import { Route, Link, routeHistory } from '@nackle/biw-web-router';
import {
    routes,
    bundleRoutingMiddleware,
    redirectMiddleware,
} from './app-routing.jsx';

// Bridge
import { Bridge } from '@nackle/bridge';
import auth from './app-auth.js';
import {
    authRouteMiddleware,
    authRequestMiddleware,
} from '@nackle/studio-tools';

// Fetch
import { applyRequestMiddleware } from './utils/fetch';

// Components
import { AppHeader, AppFooter, AppError } from '@nackle/studio-tools';

// Services
import CMXService from './services/cmx';

// Styles
import './styles/app.less';

const GOLD_COMPANY_ID = '00000000-0000-0000-0000-000000000000';

/*******************************************************************************
 *  1. App (root level component)
 */
const App = () => {
    const [ loaded, setLoaded ] = useState( false );
    const [ cmxBundleLoaded, setCmxBundleLoaded ] = useState( false );
    const [ canReadMarketPlace, setCanReadMarketPlace ] = useState( false );
    const [ canEditMarketPlace, setCanEditMarketPlace ] = useState( false );
    const [ canDeleteMarketPlace, setCanDeleteMarketPlace ] = useState( false );
    const [ permission, setPermission ] = useState();

    const handleSignIn = async ( user ) => {
        await routeHistory.refresh();
        const cmxService = new CMXService( auth.state );
        await cmxService.loadBundles( [ 'navigation', 'studio-in-context-help' ] );

        const readMarketPlace =
            user.claims.permissions[ 'catalog-sso-service' ]?.indexOf(
                'read-marketplace-list'
            ) > -1;
        const editMarketPlace =
            user.claims.permissions[ 'catalog-sso-service' ]?.indexOf(
                'edit-marketplace-list'
            ) > -1;
        const deleteMarketPlace =
            user.claims.permissions[ 'catalog-sso-service' ]?.indexOf(
                'delete-marketplace-list'
            ) > -1;

        setLoaded( true );
        setCmxBundleLoaded( true );
        setCanReadMarketPlace( readMarketPlace );
        setCanEditMarketPlace( editMarketPlace );
        setCanDeleteMarketPlace( deleteMarketPlace );
        setPermission( user.claims.permissions );
    };

    return (
        <AppError>
            <Route
                render={ ( route ) => {
                    // set the title
                    document.title =
                        route && route.match ? route.match.title : 'Home';
                    // const companyId = route.state?.params?.companyId;
                    const companyRoute = route.pathname;
                    const part = companyRoute.split( '/' );
                    const companyId = part[ 1 ];

                    return (
                        <Bridge auth={ auth } onSignIn={ handleSignIn }>
                            { loaded && (
                                <Layout className="root-layout">
                                    <AppHeader
                                        auth={ auth }
                                        companyId={ GOLD_COMPANY_ID }
                                        companySelectDisabled="true"
                                    />
                                    <Layout>    
                                        <NackleMenu
                                            auth={ auth }
                                            cmxBundleLoaded={ cmxBundleLoaded }
                                            collapsible={ true }
                                        />
                                        <Layout className="page-layout">
                                            <Content className="app-content">
                                                { route.match ? (
                                                    <div
                                                        key={
                                                            route.state.params
                                                                .companyId
                                                        }
                                                        className="app-container"
                                                    >
                                                        <div className="main-container">
                                                            { React.cloneElement(
                                                                route.component,
                                                                {
                                                                    companyId,
                                                                    route,
                                                                    canEditMarketPlace,
                                                                    canReadMarketPlace,
                                                                    canDeleteMarketPlace,
                                                                    permission,
                                                                    auth,
                                                                }
                                                            ) }
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <Result
                                                        status="404"
                                                        title="404"
                                                        subTitle="Sorry, the page you visited does not exist."
                                                        extra={
                                                            <Link
                                                                to="/"
                                                                className="ant-btn ant-btn-primary"
                                                            >
                                                                Back Home
                                                            </Link>
                                                        }
                                                    />
                                                ) }
                                            </Content>
                                            <AppFooter />
                                        </Layout>
                                    </Layout>
                                </Layout>
                            ) }
                        </Bridge>
                    );
                } }
            />
        </AppError>
    );
};

/*******************************************************************************
 *  2. Connect Routing
 */

routeHistory.applyRoutes( routes );
routeHistory.applyMiddleware( [
    authRouteMiddleware( auth ),
    bundleRoutingMiddleware,
    ...redirectMiddleware,
] );

/*******************************************************************************
 *  3. Connect Auth to Fetch
 */
applyRequestMiddleware( authRequestMiddleware );

/*******************************************************************************
 * 4. Render the the app
 */

const root = createRoot( document.getElementById( 'app-root' ) );

root.render( <App /> );
